import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { soccerPath } from '../utils/paths';
import { mediaUrl } from '../utils/blogUtils';
import './PlayerDetailProfile.css';

const formatPosition = (p) =>
  p ? p.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
const formatSkillCategory = (c) =>
  c ? c.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (x) => x.toUpperCase()) : '—';
const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatRole = (role) =>
  role ? role.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()) : '';

const HIGHLIGHT_META = {
  GOAL: { label: 'Goal', icon: '⚽', className: 'goal' },
  ASSIST: { label: 'Assist', icon: '🎯', className: 'assist' },
  MOTM: { label: 'Man of the Match', icon: '🏆', className: 'motm' },
  HAT_TRICK: { label: 'Hat-trick', icon: '🔥', className: 'hat-trick' },
  CLEAN_SHEET: { label: 'Clean Sheet', icon: '🧤', className: 'clean-sheet' },
  CUSTOM: { label: 'Award', icon: '⭐', className: 'custom' },
};

const formatEventType = (eventType, manual) => {
  if (manual || eventType === 'MANUAL') return 'Pickup / Manual';
  if (eventType === 'MATCH') return 'Match';
  return 'Practice';
};

const StatCard = ({ value, label }) => (
  <div className="player-detail-profile__stat-card">
    <span className="player-detail-profile__stat-value">{value}</span>
    <span className="player-detail-profile__stat-label">{label}</span>
  </div>
);

const PlayerDetailProfile = () => {
  const { id: clubId, playerId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoading(true);
      setError('');
      const data = await api.getPlayerClubProfile(clubId, playerId, user.id);
      setProfile(data);
    } catch (err) {
      setProfile(null);
      setError(err.message || 'Failed to load player profile');
    } finally {
      setLoading(false);
    }
  }, [clubId, playerId, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    loadProfile();
  }, [isAuthenticated, user, loadProfile]);

  if (!isAuthenticated) {
    return (
      <div className="player-detail-profile">
        <div className="alert alert-warning">Please log in to view player profiles.</div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  if (error) {
    return (
      <div className="player-detail-profile">
        <button
          type="button"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}`))}
          className="btn btn-secondary player-detail-profile__back"
        >
          ← Back to Club
        </button>
        <div className="alert alert-error">{error}</div>
      </div>
    );
  }

  if (!profile?.player) {
    return (
      <div className="player-detail-profile">
        <div className="empty-state">Player profile not found.</div>
      </div>
    );
  }

  const { player, clubName, roles, memberSince, ownProfile, canEdit, playingRecord } = profile;
  const stats = playingRecord?.stats || {};
  const highlights = playingRecord?.highlights || [];
  const photoUrl = player.profilePictureUrl ? mediaUrl(player.profilePictureUrl) : null;
  const hasStats =
    (stats.totalGoals || 0) > 0 ||
    (stats.totalAssists || 0) > 0 ||
    (stats.manOfMatchCount || 0) > 0 ||
    (stats.practicesPlayed || 0) > 0;

  return (
    <div className="player-detail-profile">
      <div className="player-detail-profile__nav">
        <button
          type="button"
          onClick={() => navigate(soccerPath(`/clubs/${clubId}`))}
          className="btn btn-secondary"
        >
          ← Back to {clubName || 'Club'}
        </button>
        {canEdit && ownProfile && (
          <Link to={soccerPath('/players/profile')} className="btn btn-primary">
            Edit My Profile
          </Link>
        )}
        {canEdit && !ownProfile && (
          <Link to={soccerPath(`/clubs/${clubId}/players/${playerId}/edit`)} className="btn btn-primary">
            Edit Profile
          </Link>
        )}
      </div>

      <div className="player-detail-profile__card">
        <div className="player-detail-profile__header">
          <div className="player-detail-profile__avatar-wrap">
            {photoUrl ? (
              <img src={photoUrl} alt={player.name} className="player-detail-profile__avatar" />
            ) : (
              <div className="player-detail-profile__avatar-placeholder">
                {player.name?.charAt(0)?.toUpperCase() || '?'}
              </div>
            )}
          </div>
          <div className="player-detail-profile__identity">
            <p className="player-detail-profile__club">{clubName}</p>
            <h1>{player.name}</h1>
            {ownProfile && <span className="badge badge-info">Your Profile</span>}
            <p className="player-detail-profile__position">
              {formatPosition(player.position)}
              {player.skillCategory && (
                <span> · {formatSkillCategory(player.skillCategory)}</span>
              )}
            </p>
            {roles && roles.length > 0 && (
              <div className="player-detail-profile__roles">
                {roles.map((role) => (
                  <span key={role} className="badge badge-info">
                    {formatRole(role)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="player-detail-profile__stats-section">
          <h2>Playing Record</h2>
          <div className="player-detail-profile__stats-grid">
            <StatCard value={stats.totalGoals || 0} label="Goals" />
            <StatCard value={stats.totalAssists || 0} label="Assists" />
            <StatCard value={stats.manOfMatchCount || 0} label="Man of the Match" />
            <StatCard value={stats.practicesPlayed || 0} label="Practices" />
          </div>
          {(stats.totalSaves > 0 || stats.totalDefended > 0) && (
            <div className="player-detail-profile__stats-extra">
              {stats.totalSaves > 0 && <span>{stats.totalSaves} saves</span>}
              {stats.totalDefended > 0 && <span>{stats.totalDefended} defended</span>}
            </div>
          )}
          {!hasStats && (
            <p className="player-detail-profile__stats-empty">
              No recorded stats yet. Goals and Man of the Match awards will appear after practices and matches.
            </p>
          )}
        </div>

        {highlights.length > 0 && (
          <div className="player-detail-profile__highlights">
            <h2>Highlights</h2>
            <ul className="player-detail-profile__timeline">
              {highlights.map((item, index) => {
                const meta = HIGHLIGHT_META[item.type] || HIGHLIGHT_META.GOAL;
                return (
                  <li
                    key={`${item.type}-${item.eventId}-${index}`}
                    className={`player-detail-profile__timeline-item player-detail-profile__timeline-item--${meta.className}`}
                  >
                    <div className="player-detail-profile__timeline-marker">{meta.icon}</div>
                    <div className="player-detail-profile__timeline-content">
                      <div className="player-detail-profile__timeline-header">
                        <span className="player-detail-profile__timeline-type">{meta.label}</span>
                        <span className="player-detail-profile__timeline-date">
                          {formatDate(item.eventDate)}
                        </span>
                      </div>
                      <p className="player-detail-profile__timeline-title">{item.eventTitle}</p>
                      {item.description && (
                        <p className="player-detail-profile__timeline-desc">{item.description}</p>
                      )}
                      <span className="player-detail-profile__timeline-badge">
                        {formatEventType(item.eventType, item.manual)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        <div className="player-detail-profile__details">
          <h2>Player Details</h2>
          <dl className="player-detail-profile__grid">
            <div>
              <dt>Position</dt>
              <dd>{formatPosition(player.position)}</dd>
            </div>
            <div>
              <dt>Skill Category</dt>
              <dd>{formatSkillCategory(player.skillCategory)}</dd>
            </div>
            <div>
              <dt>Date of Birth</dt>
              <dd>{formatDate(player.dateOfBirth)}</dd>
            </div>
            <div>
              <dt>Member Since</dt>
              <dd>{formatDate(memberSince)}</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailProfile;
